import React, { useState, useEffect } from 'react';
import { Icons as I } from '../pack-icons';

const STORAGE_KEY = 'dathent-theme';

/**
 * ThemeToggle — 3 inline buttons (light/dark/system).
 * Reads + writes localStorage 'dathent-theme'.
 * Resolves 'system' via prefers-color-scheme and applies data-theme on <html>.
 */
export default function ThemeToggle() {
  const [mode, setMode] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    // Default to current DOM state (set by FOWT script), or 'light'
    return document.documentElement.dataset.theme || 'light';
  });

  // Apply theme + persist on mode change
  useEffect(() => {
    const resolved =
      mode === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : mode;
    document.documentElement.setAttribute('data-theme', resolved);
    document.documentElement.style.colorScheme = resolved;
    if (mode === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Subscribe to OS preference when in 'system' mode
  useEffect(() => {
    if (mode !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const t = e.matches ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', t);
      document.documentElement.style.colorScheme = t;
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [mode]);

  const opts = [
    { id: 'light', Icon: I.sun },
    { id: 'dark', Icon: I.moon },
    { id: 'system', Icon: I.monitor },
  ];

  return (
    <div className="tt" role="group" aria-label="Theme">
      {opts.map((o) => (
        <button
          key={o.id}
          className={mode === o.id ? 'is-active' : ''}
          onClick={() => setMode(o.id)}
          aria-label={o.id}
          type="button"
        >
          <o.Icon size={14} />
        </button>
      ))}
    </div>
  );
}
